import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useFormik } from "formik";
import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import moment from "moment";
import * as Yup from "yup";
import { get, isEmpty, omit } from "lodash";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import InputFieldText from "Components/Common/InputFieldText";
import { dateFormatter } from "utils/dateFormatter";
import DatePicker from "Components/Common/DatePicker";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import SelectField from "Components/Common/SelectField";
import {
  getOptionValue,
  offlinePaymentSubscriptionTypes,
  paymentModeEnum,
  utilityOptions,
} from "common/enums";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import {
  filterEmptyStrings,
  getImageUrl,
  isAuthorizedUser,
  onlyNumberKeys,
} from "utils";

import {
  useLazyGetApplicationNumbers,
  useLazyGetBillDetailsWithConsumerNumber,
  usePostDemandEstimateOfflinePaymentDetailsMutation,
  usePostOfflinePaymentDetailsMutation,
} from "apis/UtilityConsumer/utilityConsumer";
import { useLazyGetDemandEstimationByApplicationIdQuery } from "apis/DemandEstimation";
import { SUPER_ADMIN } from "common/constant";

const demandEstimateSubscriptionTypeId = getOptionValue(
  "offlinePaymentSubscriptionTypes",
  "Demand Estimate"
);

const getSubscriptionType = (subscriptionType) =>
  subscriptionType ===
  getOptionValue("offlinePaymentSubscriptionTypes", "Sewage")
    ? getOptionValue("offlinePaymentSubscriptionTypes", "Water")
    : subscriptionType;

const OfflinePayment = ({ t }) => {
  const [addPaymentDetails, { isLoading: isPaymentDataAdding }] =
    usePostOfflinePaymentDetailsMutation();

  const [
    addDemandEstimatePaymentDetails,
    { isLoading: isDemandEstimatePaymentDataAdding },
  ] = usePostDemandEstimateOfflinePaymentDetailsMutation();

  const [
    getConsumerBillDetails,
    { data: { data: consumerNumbers } = {}, isLoading, isFetching },
  ] = useLazyGetBillDetailsWithConsumerNumber();

  const [
    getApplicationNumbers,
    {
      data: { data: applicationNumbers } = {},
      isLoading: isApplicationNumbersLoading,
    },
  ] = useLazyGetApplicationNumbers();

  const [
    getDemandEstimateDetails,
    {
      isLoading: isDemandEstimateDataLoading,
      isCatching: isDemandEstimateDataCatching,
    },
  ] = useLazyGetDemandEstimationByApplicationIdQuery();

  const { utilityRoles } = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(
    getOptionValue("subscriptionTypes", "Electricity")
  );
  const [isPowerUtility, setIsPowerUtility] = useState(false);
  const [{ currentIgst, currentSgst, currentCgst }, setGSTAmount] = useState({
    currentIgst: 0,
    currentSgst: 0,
    currentCgst: 0,
  });
  const [charges, setCharges] = useState(null);
  const [isOtherUtilitySubscription, setIsOtherUtilitySubscription] =
    useState(false);

  const updatedOfflinePaymentSubscriptionTypes = useMemo(
    () =>
      offlinePaymentSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(allowedRoles, roles)
      ),
    [offlinePaymentSubscriptionTypes]
  );

  const updateUtilityOptions = useMemo(
    () =>
      roles.includes(SUPER_ADMIN)
        ? utilityOptions
        : utilityOptions.filter(({ value }) =>
            utilityRoles.some(({ utilityId }) => utilityId === value)
          ),
    [roles]
  );

  const initialValues = useMemo(
    () =>
      charges
        ? Object.entries(charges).reduce(
            (acc, [key, value]) => {
              if (value > 0) {
                acc[key] = value;
                acc[`${key}Tds`] = 0;
                if (!isPowerUtility) {
                  acc[`${key}TdsYear`] = moment();
                }
              }
              return acc;
            },
            { ...(isPowerUtility && { tdsYear: moment() }) }
          )
        : { tdsYear: moment() },
    [charges, isPowerUtility]
  );

  const validationSchema = useMemo(
    () =>
      charges
        ? Object.entries(charges).reduce(
            (acc, [key, value]) => {
              if (value > 0) {
                const capitalizeKey = convertCamelTextToCapitalizeText(key);

                acc[`${key}Tds`] = Yup.number()
                  .required(`${capitalizeKey} TDS amount is required`)
                  .test(`isValid${key}TDSAmount`, "", function () {
                    const [{ value: current = null } = {}] = this.from || [];

                    if (current[`${key}Tds`] < 0) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must greater than 0`,
                      });
                    }

                    if (current[key] < current[`${key}Tds`]) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must be less than ${capitalizeKey}`,
                      });
                    }

                    return true;
                  });

                if (!isPowerUtility) {
                  acc[`${key}TdsYear`] = Yup.date().test(
                    "isValidYear",
                    "",
                    function () {
                      const [{ value: current = {} } = {}] = this.from || [];
                      if (!current[`${key}TdsYear`]) {
                        return this.createError({
                          message: "Select financial year",
                        });
                      }
                      return true;
                    }
                  );
                }
              }
              return acc;
            },
            {
              ...(isPowerUtility && {
                tdsYear: Yup.date().test("isValidYear", "", function () {
                  const [{ value: current = {} } = {}] = this.from || [];
                  if (!current.tdsYear) {
                    return this.createError({
                      message: "Select financial year",
                    });
                  }
                  return true;
                }),
              }),
            }
          )
        : {},
    [charges, isPowerUtility]
  );

  const formik = useFormik({
    initialValues: {
      tdsYear: moment(),
      applicationId: "",
      utilityId: "",
      tdsAmount: "",
      consumerName: "",
      billNumber: "",
      consumerNumber: "",
      billCalculationIds: [],
      billCycle: "",
      payableAmount: "",
      amount: "",
      currentConsumerNumber: "",
      demandEstimateNumber: "",
      paymentMode: "",
      refNumber: "",
    },
    validationSchema: Yup.object({
      ...validationSchema,
      consumerNumber: Yup.string().test(
        "isValidConsumerNumber",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];

          if (
            subscriptionType !== demandEstimateSubscriptionTypeId &&
            !current.consumerNumber
          ) {
            return this.createError({
              message: "Consumer Number is required",
            });
          }

          return true;
        }
      ),
      cgstAmount: Yup.number().test("isValidCgstAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (!current.cgstAmount && isOtherUtilitySubscription && currentCgst) {
          return this.createError({
            message: "CGST amount is required",
          });
        }
        return true;
      }),
      igstAmount: Yup.number().test("isValidIgstAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (!current.igstAmount && isOtherUtilitySubscription && currentIgst) {
          return this.createError({
            message: "IGST amount is required",
          });
        }
        return true;
      }),
      sgstAmount: Yup.number().test("isValidSgstAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (!current.sgstAmount && isOtherUtilitySubscription && currentSgst) {
          return this.createError({
            message: "SGST amount is required",
          });
        }
        return true;
      }),
      cgstTdsAmount: Yup.number().test("isValidCgstTDSAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (
          current.cgstAmount &&
          isOtherUtilitySubscription &&
          current.cgstTdsAmount > current.cgstAmount
        ) {
          return this.createError({
            message: "CGST TDS amount must be less than CGST amount",
          });
        }
        return true;
      }),
      igstTdsAmount: Yup.number().test("isValidIgstTDSAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (
          current.igstAmount &&
          isOtherUtilitySubscription &&
          current.igstTdsAmount > current.igstAmount
        ) {
          return this.createError({
            message: "IGST TDS amount must be less than IGST amount",
          });
        }
        return true;
      }),
      sgstTdsAmount: Yup.number().test("isValidSgstTDSAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (
          current.sgstAmount &&
          isOtherUtilitySubscription &&
          current.sgstTdsAmount > current.sgstAmount
        ) {
          return this.createError({
            message: "SGST TDS amount must be less than SGST amount",
          });
        }
        return true;
      }),
      amount: Yup.number().test("isValidAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          subscriptionType !== demandEstimateSubscriptionTypeId &&
          !current.amount &&
          !isOtherUtilitySubscription
        ) {
          return this.createError({
            message: "Amount is required",
          });
        }

        return true;
      }),
      tdsAmount: Yup.number().test("isValidTDSAmount", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          current.tdsAmount > current.amount &&
          subscriptionType !== demandEstimateSubscriptionTypeId
        ) {
          return this.createError({
            message: "TDS amount must be less than amount",
          });
        }

        return true;
      }),
      utilityId: Yup.number().test("isValidUtilityId", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          subscriptionType === demandEstimateSubscriptionTypeId &&
          !current.utilityId
        ) {
          return this.createError({
            message: "Utility is required",
          });
        }

        return true;
      }),
      applicationId: Yup.number().test("isValidApplicationId", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          subscriptionType === demandEstimateSubscriptionTypeId &&
          !current.applicationId
        ) {
          return this.createError({
            message: "Application number is required",
          });
        }

        return true;
      }),
      paymentMode: Yup.string().required("Payment mode is required"),
      refNumber: Yup.string().test("isRefNumberRequired", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          current.paymentMode !== getOptionValue("paymentModeEnum", "Cash") &&
          !current.refNumber
        ) {
          return this.createError({
            message: "Reference number is required",
          });
        }
        return true;
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      const {
        amount,
        paymentMode,
        refNumber,
        billCalculationIds,
        tdsAmount,
        tdsYear,
      } = values;
      try {
        if (subscriptionType === demandEstimateSubscriptionTypeId) {
          const payload = {
            ...values,
            subscriptionType: getSubscriptionType(subscriptionType),
          };

          Object.entries(charges).forEach(([key, value]) => {
            if (value > 0) {
              payload[`${key}TdsYear`] = isPowerUtility
                ? tdsYear.format("YYYY")
                : get(values, `${key}TdsYear`).format("YYYY");
            }
          });

          const { data: { receiptUrl } = {} } =
            await addDemandEstimatePaymentDetails(
              omit(filterEmptyStrings(payload), ["tdsYear"])
            );
          setDocumentToPreview({
            url: getImageUrl(receiptUrl),
            documentTypeName: "Bill Payment Receipt",
            documentType: "pdf",
            documentName: "bill",
          });
        } else {
          const payload = filterEmptyStrings({
            amount,
            paymentMode,
            tdsAmount: tdsAmount || 0,
            tdsYear: tdsYear.format("YYYY"),
            refNumber,
            subscriptionType: getSubscriptionType(subscriptionType),
            ...(isOtherUtilitySubscription && {
              cgstAmount: values.cgstAmount,
              igstAmount: values.igstAmount,
              sgstAmount: values.sgstAmount,
              cgstTdsAmount: values.cgstTdsAmount || 0,
              cgstTdsYear: values.cgstTdsYear
                ? values.cgstTdsYear.format("YYYY")
                : "",
              igstTdsAmount: values.igstTdsAmount || 0,
              igstTdsYear: values.igstTdsYear
                ? values.igstTdsYear.format("YYYY")
                : "",
              sgstTdsAmount: values.sgstTdsAmount || 0,
              sgstTdsYear: values.sgstTdsYear
                ? values.sgstTdsYear.format("YYYY")
                : "",
            }),
          });

          const { data: { receiptUrl } = {} } = await addPaymentDetails({
            ...payload,
            billCalculationIds,
          });
          setDocumentToPreview({
            url: getImageUrl(receiptUrl),
            documentTypeName: "Bill Payment Receipt",
            documentType: "pdf",
            documentName: "bill",
          });
        }
        setCharges(null);
        setSubscriptionType(getOptionValue("subscriptionTypes", "Electricity"));
        resetForm();
      } catch (error) {}
    },
  });

  const {
    values: {
      paymentMode,
      consumerNumber,
      tdsYear,
      utilityId,
      payableAmount,
      cgstTdsYear,
      sgstTdsYear,
      igstTdsYear,
    },
    values,
    setFieldValue,
    handleSubmit,
  } = formik;

  const isDemandEstimateSubscriptionType = useMemo(
    () => demandEstimateSubscriptionTypeId === subscriptionType,
    [subscriptionType]
  );

  const consumerNumberChangeHandler = ({
    consumerNumber,
    billingCalculationIds,
    billCycle,
    consumerName,
    billNumber,
    netPayable,
    igst,
    sgst,
    cgst,
    payableAmount,
  }) => {
    setFieldValue("consumerNumber", consumerNumber);
    setFieldValue("billCalculationIds", billingCalculationIds);
    setFieldValue("billCycle", billCycle);
    setFieldValue("consumerName", consumerName);
    setFieldValue("billNumber", billNumber);
    setFieldValue("payableAmount", netPayable);
    setFieldValue("amount", payableAmount);
    if (isOtherUtilitySubscription) {
      if (igst) {
        setFieldValue("igstAmount", igst);
        setFieldValue("igstTdsAmount", "");
        setFieldValue("igstTdsYear", moment());
      }
      if (sgst) {
        setFieldValue("sgstAmount", sgst);
        setFieldValue("sgstTdsAmount", "");
        setFieldValue("sgstTdsYear", moment());
      }
      if (cgst) {
        setFieldValue("cgstAmount", cgst);
        setFieldValue("cgstTdsAmount", "");
        setFieldValue("cgstTdsYear", moment());
      }
      setGSTAmount({ currentIgst: igst, currentCgst: cgst, currentSgst: sgst });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (isDemandEstimateSubscriptionType && utilityId) {
          await getApplicationNumbers(getSubscriptionType(subscriptionType), {
            utilityId,
          });
        } else if (subscriptionType && !isDemandEstimateSubscriptionType) {
          await getConsumerBillDetails(getSubscriptionType(subscriptionType), {
            utilityId:
              offlinePaymentSubscriptionTypes.find(
                ({ value }) => value === subscriptionType
              ).utilityId ?? "",
          });
        }
      } catch (error) {}
    })();
  }, [subscriptionType, isDemandEstimateSubscriptionType, utilityId]);

  useEffect(() => {
    if (!isEmpty(initialValues)) {
      Object.entries(initialValues).forEach(([key, value]) =>
        setFieldValue(key, value)
      );
    }
  }, [initialValues]);

  const inputChangeHandler = async (enteredConsumerNumber) => {
    setFieldValue("consumerNumber", "");

    try {
      if (subscriptionType) {
        await getConsumerBillDetails(getSubscriptionType(subscriptionType), {
          consumerNumber: enteredConsumerNumber,
          utilityId:
            offlinePaymentSubscriptionTypes.find(
              ({ value }) => value === subscriptionType
            ).utilityId ?? "",
        });
      }
    } catch (error) {}
  };

  const applicationIdInputChangeHandler = async (enteredConsumerNumber) => {
    setFieldValue("applicationId", "");

    try {
      await getApplicationNumbers(getSubscriptionType(subscriptionType), {
        utilityId,
        applicationNumber: enteredConsumerNumber,
      });
    } catch (error) {}
  };

  const applicationIdChangeHandler = async (option) => {
    const selectedOptionDetails =
      applicationNumbers?.find(({ id }) => id === option.value) ?? {};
    try {
      setFieldValue("applicationId", selectedOptionDetails.id ?? "");
      setFieldValue(
        "currentConsumerNumber",
        selectedOptionDetails.consumerNumber ?? ""
      );
      setFieldValue("consumerName", selectedOptionDetails.consumerName ?? "");
      setFieldValue(
        "demandEstimateNumber",
        selectedOptionDetails.demandEstimateNumber ?? ""
      );
      setFieldValue("payableAmount", selectedOptionDetails.totalAmount ?? "");
      const { data } = await getDemandEstimateDetails(
        selectedOptionDetails.id ?? ""
      );
      setCharges(data.charges);
    } catch (error) {}
  };

  useEffect(() => {
    setIsPowerUtility(
      [
        getOptionValue("offlinePaymentSubscriptionTypes", "Electricity"),
        getOptionValue("offlinePaymentSubscriptionTypes", "Security Deposit"),
        getOptionValue("offlinePaymentSubscriptionTypes", "Backup Power"),
      ].includes(subscriptionType) ||
        getOptionValue("utilityOptions", "Power") === utilityId
    );

    setIsOtherUtilitySubscription(
      [
        getOptionValue("offlinePaymentSubscriptionTypes", "DCS"),
        getOptionValue("offlinePaymentSubscriptionTypes", "Water"),
        getOptionValue("offlinePaymentSubscriptionTypes", "Sewage"),
        getOptionValue("offlinePaymentSubscriptionTypes", "Solid Waste"),
      ].includes(subscriptionType)
    );
  }, [subscriptionType, utilityId]);

  return (
    <div className="page-content h-100">
      <Backdrop
        open={
          isLoading ||
          isFetching ||
          isDemandEstimateDataLoading ||
          isApplicationNumbersLoading ||
          isDemandEstimateDataCatching
        }
      />
      <BreadCrumb title={t("Offline Payment")} pageTitle={t("Payment")} />

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SelectField
            label={t("Subscription Type") + "*"}
            value={updatedOfflinePaymentSubscriptionTypes.find(
              ({ value }) => value === subscriptionType
            )}
            onChange={(_, { value }) => {
              setSubscriptionType(value);
              setFieldValue("igstAmount", "");
              setFieldValue("sgstAmount", "");
              setFieldValue("cgstAmount", "");
              setFieldValue("cgstTdsAmount", "");
              setFieldValue("cgstTdsYear", "");
              setFieldValue("igstTdsAmount", "");
              setFieldValue("igstTdsYear", "");
              setFieldValue("sgstTdsAmount", "");
              setFieldValue("sgstTdsYear", "");
            }}
            options={updatedOfflinePaymentSubscriptionTypes}
          />
        </Grid>
        {isDemandEstimateSubscriptionType && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Utility") + "*"}
                name="utilityId"
                onChange={(_, { value }) => {
                  setFieldValue("utilityId", value);
                  setCharges(null);
                }}
                formik={formik}
                options={updateUtilityOptions}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Application Number") + "*"}
                name="applicationId"
                disabled={!utilityId}
                onChange={(_, option) => applicationIdChangeHandler(option)}
                formik={formik}
                onInputChange={(_, val) => applicationIdInputChangeHandler(val)}
                options={applicationNumbers ?? []}
                getOptionLabel={({ applicationNumber }) => applicationNumber}
                getOptionValue={({ id }) => id}
              />
            </Grid>
          </>
        )}
        {!isDemandEstimateSubscriptionType && (
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label={t("Consumer Number") + "*"}
              name="consumerNumber"
              formik={formik}
              options={consumerNumbers || []}
              onInputChange={(_, val) => inputChangeHandler(val)}
              onChange={(_, { value }) => {
                consumerNumberChangeHandler(
                  consumerNumbers.find(
                    ({ consumerNumber }) => consumerNumber === value
                  )
                );
              }}
              getOptionLabel={({ consumerNumber }) => consumerNumber}
              getOptionValue={({ consumerNumber }) => consumerNumber}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography className="mb-3" lineHeight="1" variant="h6">
            {t("Consumer Details")}:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label={t("Consumer Name")}
            disabled
            name="consumerName"
            formik={formik}
            placeholder="Enter Consumer Name"
          />
        </Grid>
        {!isDemandEstimateSubscriptionType && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Bill Cycle")}
                disabled
                name="billCycle"
                formik={formik}
                placeholder="Enter Bill Cycle"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Bill Number")}
                disabled
                name="billNumber"
                formik={formik}
                placeholder="Enter Bill Number"
              />
            </Grid>
          </>
        )}
        {isDemandEstimateSubscriptionType && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Consumer Number")}
                disabled
                name="currentConsumerNumber"
                formik={formik}
                placeholder="Enter Consumer Number"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Demand Estimate Number")}
                disabled
                name="demandEstimateNumber"
                formik={formik}
                placeholder="Enter Demand Estimate Number"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label={t("Net Payable")}
            name="payableAmount"
            disabled
            type="number"
            formik={formik}
            placeholder="Enter Net Payable"
            InputProps={{
              inputProps: {
                min: 0,
                onKeyDown: (e) => onlyNumberKeys(e, true),
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography className="mb-3" lineHeight="1" variant="h6">
            {t("Payment Details")}:
          </Typography>
        </Grid>
        {!!charges &&
          isDemandEstimateSubscriptionType &&
          Object.entries(charges).map(([key, value]) => (
            <>
              {value > 0 && (
                <>
                  <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                    <InputFieldText
                      label={`${convertCamelTextToCapitalizeText(key)}*`}
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name={key}
                      placeholder={`Enter ${convertCamelTextToCapitalizeText(
                        key
                      )}`}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                    <InputFieldText
                      label={`${convertCamelTextToCapitalizeText(
                        key
                      )} TDS Amount*`}
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name={`${key}Tds`}
                      placeholder={`Enter ${convertCamelTextToCapitalizeText(
                        key
                      )} TDS Amount`}
                    />
                  </Grid>
                  {!isPowerUtility && (
                    <Grid item xs={12} sm={6} md={4}>
                      <DatePicker
                        name={`${key}TdsYear`}
                        formik={formik}
                        label={`${convertCamelTextToCapitalizeText(
                          key
                        )} Financial Year*`}
                        views={["year"]}
                        format={dateFormatter(get(values, `${key}TdsYear`))}
                      />
                    </Grid>
                  )}
                </>
              )}
            </>
          ))}
        {!isDemandEstimateSubscriptionType && (
          <>
            <Grid item xs={12} sm={6} md={isOtherUtilitySubscription ? 4 : 6}>
              <InputFieldText
                label={t("Payable Amount*")}
                disabled={!consumerNumber || !subscriptionType}
                name="amount"
                formik={formik}
                type="number"
                placeholder="Enter Amount"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={isOtherUtilitySubscription ? 4 : 6}>
              <InputFieldText
                label={t("TDS Amount")}
                disabled={!consumerNumber || !subscriptionType}
                name="tdsAmount"
                formik={formik}
                type="number"
                placeholder={t("Enter TDS Amount")}
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
          </>
        )}
        {(isPowerUtility || isOtherUtilitySubscription) && (
          <Grid item xs={12} sm={6} md={isOtherUtilitySubscription ? 4 : 6}>
            <DatePicker
              name="tdsYear"
              formik={formik}
              disabled={!payableAmount}
              label={t("Financial Year") + "*"}
              views={["year"]}
              format={dateFormatter(tdsYear)}
            />
          </Grid>
        )}

        {isOtherUtilitySubscription && (
          <>
            {!!currentCgst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="CGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="cgstAmount"
                    placeholder="Enter CGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="CGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="cgstTdsAmount"
                    placeholder="Enter CGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="CGST Financial Year"
                    name="cgstTdsYear"
                    formik={formik}
                    label={t("CGST Financial Year*")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(cgstTdsYear)}
                  />
                </Grid>
              </>
            )}
            {!!currentIgst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="IGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="igstAmount"
                    placeholder="Enter IGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="IGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="igstTdsAmount"
                    placeholder="Enter IGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="IGST Financial Year"
                    name="igstTdsYear"
                    formik={formik}
                    label={t("IGST Financial Year*")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(igstTdsYear)}
                  />
                </Grid>
              </>
            )}
            {!!currentSgst && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="SGST Amount*"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="sgstAmount"
                    placeholder="Enter SGST Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label="SGST TDS Amount"
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                    formik={formik}
                    name="sgstTdsAmount"
                    placeholder="Enter SGST TDS Amount"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                    placeholder="SGST Financial Year"
                    name="sgstTdsYear"
                    formik={formik}
                    label={t("SGST Financial Year*")}
                    className="w-100"
                    views={["year"]}
                    format={dateFormatter(sgstTdsYear)}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        <Grid item xs={12} sm={6} md={isOtherUtilitySubscription ? 4 : 6}>
          <SelectField
            label={t("Payment Mode") + "*"}
            disabled={!payableAmount}
            name="paymentMode"
            formik={formik}
            options={paymentModeEnum || []}
          />
        </Grid>
        {!!paymentMode &&
          paymentMode !== getOptionValue("paymentModeEnum", "Cash") && (
            <Grid item xs={12} sm={6} md={isOtherUtilitySubscription ? 4 : 6}>
              <InputFieldText
                label={t("Reference Number") + "*"}
                disabled={!payableAmount}
                name="refNumber"
                formik={formik}
                placeholder={t("Enter Reference Number")}
              />
            </Grid>
          )}
      </Grid>
      <Grid mt={1}>
        <Button
          color="primary"
          size="small"
          onClick={handleSubmit}
          variant="contained"
          type="submit"
          disabled={isPaymentDataAdding || isDemandEstimatePaymentDataAdding}
        >
          {t("Submit")}
          {(isPaymentDataAdding || isDemandEstimatePaymentDataAdding) && (
            <CircularProgress size={20} />
          )}
        </Button>
      </Grid>
    </div>
  );
};

OfflinePayment.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OfflinePayment);
